import { $staffApi } from "../../../../api";
import { Button, Dialog, Row, TextInput } from "@narmi/design_system";
import { useForm, useFormField } from "@hooks/useForm";
import { useQueryClient } from "@tanstack/react-query";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import styles from "./style.module.css";
import { SavedReply } from "../types";
import { ExpandingTextArea } from "@components/ExpandingTextArea";

export function AddEditModal({
  reply,
  isOpen,
  handleClose,
}: {
  reply?: Partial<SavedReply>;
  isOpen: boolean;
  handleClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { sendNotification } = useNotificationContext();

  const name = useFormField(reply?.name || "", {
    validator: (value) => {
      if (!value) {
        return "Required.";
      }
      if (value.length > 128) {
        return "Name must be 128 characters or less.";
      }
      return null;
    },
  });
  const body = useFormField(reply?.body || "", {
    validator: (value) => (value ? null : "Required."),
  });

  function handleSubmitSuccess() {
    queryClient.invalidateQueries({
      queryKey: ["get", "/api/saved_replies/"],
    });
    sendNotification({
      type: "success",
      text: reply?.uuid ? "Saved reply updated." : "Saved reply added successfully.",
    });
    close();
  }

  function close() {
    form.reset();
    handleClose();
  }

  const { mutate: postSavedReply } = $staffApi.useMutation("post", "/api/saved_replies/", {
    onSuccess: () => {
      handleSubmitSuccess();
    },
    onError: (error) => {
      sendNotification({
        type: "negative",
        text: error.body || "An error occurred while adding the saved reply.",
      });
    },
  });

  const { mutate: putSavedReply } = $staffApi.useMutation("put", "/api/saved_replies/{uuid}/", {
    onSuccess: () => {
      handleSubmitSuccess();
    },
    onError: (error) => {
      sendNotification({
        type: "negative",
        text: error.body || "An error occurred while updating the saved reply.",
      });
    },
  });

  const form = useForm(
    {
      name,
      body,
    },
    {
      onSubmit: () => {
        if (reply?.uuid) {
          putSavedReply({
            params: {
              path: {
                uuid: reply.uuid,
              },
            },
            body: {
              name: name.value,
              body: body.value,
            },
          });
        } else {
          postSavedReply({
            body: {
              name: name.value,
              body: body.value,
            },
          });
        }
      },
    }
  );

  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={close}
      title={reply?.uuid ? "Edit saved reply" : "Add saved reply"}
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button label="Cancel" kind="negative" onClick={close} />
          </Row.Item>
          <Row.Item shrink>
            <Button label="Submit" onClick={() => form.submit()} />
          </Row.Item>
        </Row>
      }
    >
      <div className={styles.replyForm}>
        <TextInput
          label="Saved reply name"
          value={name.value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => name.setValue(e.target.value)}
          error={name.error}
        />
        <ExpandingTextArea
          label="Body"
          value={body.value}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => body.setValue(e.target.value)}
          error={body.error}
        />
      </div>
    </Dialog>
  );
}
